
import Component from "vue-class-component"
import PageHeader from "@/superannuation/PageHeader.vue"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import {Company} from "@/superannuation/PlanService"
import SelectDropDown from "@/components/SelectDropDown.vue"
import ErrorMessage from "@/components/ErrorMessage.vue"
import {theme} from "@/theme"
import {QFile, QPopupProxy} from "quasar"
import Axios, {AxiosError, AxiosResponse} from "axios"
import {format, formatISO, startOfMonth, subDays} from "date-fns"
import {restDownloadFile} from "@/utils/rest"
import CloseButton from "@/components/CloseButton.vue"

export interface SalesforceBatchJob {
  batchId: number
  executionId: number
  startDate: string
  atDate: string
  providerId: string
  employerId: string | null
  provider: string
  batchStatus: string
  runDate: string
  exported: number
  jobId: string
  jobStatus: string
  imported: number
  failed: number
}

export interface Provider {
  id: string
  name: string
}

@Component({
  components: {
    ErrorMessage,
    SelectDropDown,
    PageHeader,
    HelpIcon,
    QPopupProxy,
    QFile,
    CloseButton,
  },
})
export default class SalesforceView extends Vue {
  t = theme()

  readonly runningStatuses = ["STARTING", "STARTED"]

  readonly executionMonths = [
    {label: "Include previous month", value: "previous"},
    {label: "Current month", value: "current"},
    {label: "Yesterday", value: "day"},
    {label: "Today", value: "today"},
  ]

  readonly visibleColumns = [
    "batchId",
    "atDate",
    "provider",
    "batchStatus",
    "runDate",
    "exported",
    "jobId",
    "jobStatus",
    "imported",
    "failed",
  ]

  $refs: {
    providerSelect: SelectDropDown
    companySelect: SelectDropDown
  }

  providerId: string | null = null
  companyId: string | null = null

  records: Array<SalesforceBatchJob> = new Array<SalesforceBatchJob>()
  selectedRecords: Array<SalesforceBatchJob> = new Array<SalesforceBatchJob>()

  message: string | null = null

  retryFile: File | null = null

  dataInProgress = false
  requestInProgress = false

  running = false
  stopped = false
  failed = false
  complete = false
  showOperations = false
  fullHistory = false

  executeMonth = "previous"

  get isNoneSelected(): boolean {
    return this.selectedRecords.length === 0
  }

  get isOneSelected(): boolean {
    return this.selectedRecords.length === 1
  }

  get isOneWithFailedSelected(): boolean {
    return (
      this.isOneSelected &&
      this.selectedRecords.some(
        batchJob => batchJob.failed > 0 && batchJob.jobStatus === "JobComplete"
      )
    )
  }

  get isAnyStoppedSelected(): boolean {
    return this.selectedRecords.some(record => this.runningStatuses.includes(record.batchStatus))
  }

  async created() {
    await this.getBatchJobs()
  }

  private providerSelected(provider: Provider | null) {
    this.providerId = provider == null ? null : provider.id
    if (provider != null) {
      this.$refs.companySelect.model = null
      this.companyId = null
    }
  }

  private companySelected(company: Company | null) {
    if (company == null) {
      this.companyId = null
    } else {
      this.companyId = company.id
    }
  }

  private companiesPath(): string | null {
    return this.providerId == null ? null : `/api/providers/${this.providerId}/companies`
  }

  private async refresh(done: () => void) {
    await this.getBatchJobs()
    done()
  }

  private async getBatchJobs() {
    this.dataInProgress = true
    await Axios.get(`/api/salesforce/batches`, {
      params: {fullHistory: this.fullHistory},
    })
      .then((response: AxiosResponse) => {
        this.message = null
        this.records = response.data
      })
      .catch((reason: AxiosError<any>) => {
        this.message = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  private async downloadFiles() {
    if (!this.isNoneSelected) {
      this.requestInProgress = true
      const now = format(new Date(), "ddMMyyyy")
      await restDownloadFile(`/api/salesforce/exports/files`, `SALESFORCE-EXPORTS-${now}.zip`, {
        jobIds: this.selectedRecords.map(job => job.batchId).join(),
      })
      this.requestInProgress = false
    }
  }

  private async executeBatch() {
    this.showOperations = false
    if (this.isOneSelected) {
      this.requestInProgress = true
      const job = this.selectedRecords.find(Boolean)!
      const form = new FormData()
      form.set("startDate", job.startDate)
      form.set("endDate", job.atDate)
      form.set("providerIdFilter", job.providerId)
      if (job.employerId != null) {
        form.set("employerIdFilter", job.employerId)
      }
      void Axios.post(`/api/salesforce/exports`, form)
        .then((this.message = null))
        .catch((reason: AxiosError<any>) => {
          this.message = reason.response?.data.message
        })
        .finally(() => (this.requestInProgress = false))
    }
  }

  private async retryFailed() {
    this.showOperations = false
    if (this.isOneWithFailedSelected) {
      this.requestInProgress = true
      const job = this.selectedRecords.find(Boolean)!
      const form = new FormData()
      form.set("jobId", job.batchId.toString())
      if (this.retryFile !== null) {
        form.set("file", this.retryFile)
      }

      void Axios.patch(`/api/salesforce/retry`, form)
        .then((this.message = null))
        .catch((reason: AxiosError<any>) => {
          this.message = reason.response?.data.message
        })
        .finally(() => (this.requestInProgress = false))
    }
  }

  private async stopJobs() {
    this.requestInProgress = true
    const form = new FormData()
    if (!this.isNoneSelected) {
      form.set("jobIds", this.selectedRecords.map(job => job.batchId).join())
    }
    void Axios.patch(`/api/salesforce/exports`, form)
      .then((this.message = null))
      .catch((reason: AxiosError<any>) => {
        this.message = reason.response?.data.message
      })
      .finally(() => (this.requestInProgress = false))
  }

  private async cleanBatches() {
    this.requestInProgress = true
    void Axios.delete(`/api/salesforce/batches`, {
      params: {running: this.running, stopped: this.stopped},
    })
      .then((this.message = null))
      .catch((reason: AxiosError<any>) => {
        this.message = reason.response?.data.message
      })
      .finally(() => (this.requestInProgress = false))
  }

  private async cleanJobs() {
    this.requestInProgress = true
    void Axios.delete(`/api/salesforce/exports`, {
      params: {failed: this.failed, complete: this.complete},
    })
      .then(() => (this.message = null))
      .catch((reason: AxiosError<any>) => {
        this.message = reason.response?.data.message
      })
      .finally(() => (this.requestInProgress = false))
  }

  private async executeBatches() {
    this.requestInProgress = true

    const form = new FormData()
    if (this.providerId != null) {
      form.set("providerIdFilter", this.providerId)
    }

    if (this.companyId != null) {
      form.set("employerIdFilter", this.companyId)
    }

    if (this.executeMonth === "current") {
      form.set(
        "startDate",
        formatISO(subDays(startOfMonth(Date.now()), 1), {
          representation: "date",
        })
      )
    } else if (this.executeMonth === "day") {
      form.set("startDate", formatISO(subDays(Date.now(), 1), {representation: "date"}))
    } else if (this.executeMonth === "today") {
      const today = formatISO(Date.now(), {representation: "date"})
      form.set("startDate", today)
      form.set("endDate", today)
    }

    form.set("forceNewRun", "true")

    void Axios.post(`/api/salesforce/exports`, form)
      .then((this.message = null))
      .catch((reason: AxiosError<any>) => {
        this.message = reason.response?.data.message
      })
      .finally(() => (this.requestInProgress = false))
  }

  private showJobOperations(job: SalesforceBatchJob) {
    this.showOperations = true
    this.selectedRecords = [job]
  }
}
